import type {
  Expression,
  PathExpression,
  RuleClause,
  RuleTree,
} from '@/components/shared/NewRuleTree/types'
import type { Option } from '@/types/shared.types'
import { RuleType } from '@/components/shared/NewRuleTree/types'

type AstNode =
  | { kind: 'function'; name: string; args: AstNode[] }
  | { kind: 'field'; value: string }
  | { kind: 'raw'; value: string }
  | { kind: 'string'; value: string }
  | { kind: 'number'; value: string }
  | { kind: 'bool'; value: boolean }
  | { kind: 'null' }

const comparisonOperators = ['>=', '<=', '==', '!=', '>', '<'] as const
const infixOperatorMap: Record<string, string> = {
  '==': 'EqualNoCase',
  '!=': 'NotEqual',
  '>': 'Greater',
  '>=': 'GreaterOrEqual',
  '<': 'Less',
  '<=': 'LessOrEqual',
}

const operatorNames: Record<string, string> = {
  contains: 'ContainsNoCase',
  containsnocase: 'ContainsNoCase',
  endswith: 'EndsWith',
  endswithnocase: 'EndsWithNoCase',
  equal: 'Equal',
  equalnocase: 'EqualNoCase',
  greater: 'Greater',
  greaterorequal: 'GreaterOrEqual',
  hasmember: 'HasMember',
  hasresource: 'HasResource',
  isregexmatch: 'IsRegexMatch',
  less: 'Less',
  lessorequal: 'LessOrEqual',
  notequal: 'NotEqual',
  notequalnocase: 'NotEqualNoCase',
  startswith: 'StartsWithNoCase',
  startswithnocase: 'StartsWithNoCase',
}

const treeKinds: Record<string, RuleTree['Kind']> = {
  and: 'And',
  not: 'Not',
  or: 'Or',
}
const serializableTreeKinds = new Set(['And', 'Not', 'Or'])

class RuleExpressionParser {
  private index = 0

  constructor(private readonly rule: string) {}

  parse(): AstNode | null {
    const expression = this.parseOr()
    if (!expression) return null

    this.skipTrivia()
    return this.isEnd() ? expression : null
  }

  private parseOr(): AstNode | null {
    const first = this.parseAnd()
    if (!first) return null

    const args = [first]
    while (true) {
      this.skipTrivia()
      if (!this.consume('||')) break

      const next = this.parseAnd()
      if (!next) return null
      args.push(next)
    }

    return args.length === 1 ? first : { kind: 'function', name: 'Or', args }
  }

  private parseAnd(): AstNode | null {
    const first = this.parseComparison()
    if (!first) return null

    const args = [first]
    while (true) {
      this.skipTrivia()
      if (!this.consume('&&')) break

      const next = this.parseComparison()
      if (!next) return null
      args.push(next)
    }

    return args.length === 1 ? first : { kind: 'function', name: 'And', args }
  }

  private parseComparison(): AstNode | null {
    const left = this.parsePrimary()
    if (!left) return null

    this.skipTrivia()
    const operator = comparisonOperators.find((candidate) =>
      this.rule.startsWith(candidate, this.index),
    )
    if (!operator) return left

    this.index += operator.length
    const right = this.parsePrimary()
    if (!right) return null

    return { kind: 'function', name: infixOperatorMap[operator], args: [left, right] }
  }

  private parsePrimary(): AstNode | null {
    this.skipTrivia()
    if (this.isEnd()) return null

    if (this.rule.startsWith('$"', this.index)) return this.parseParamString()
    if (this.current() === '"') return this.parseString()
    if (this.current() === '(') return this.parseParenthetical()
    if (this.current() === '[') return this.parseRawDelimited('[', ']')
    if (this.current() === '{') return this.parseRawDelimited('{', '}')

    const literal = this.parseLiteral()
    if (literal) return literal

    const functionNode = this.parseFunction()
    if (functionNode) return functionNode

    return this.parseField()
  }

  private parseParenthetical(): AstNode | null {
    this.index += 1
    const expression = this.parseOr()
    if (!expression) return null

    this.skipTrivia()
    if (this.current() !== ')') return null

    this.index += 1
    return expression
  }

  private parseFunction(): AstNode | null {
    const match = this.remaining().match(/^(\w+)\(/)
    if (!match) return null

    const name = match[1]
    this.index += name.length + 1

    const args: AstNode[] = []
    this.skipTrivia()
    if (this.current() === ')') {
      this.index += 1
      return { kind: 'function', name, args }
    }

    while (!this.isEnd()) {
      this.consumeNamedParameter()

      const argument = this.parseOr()
      if (!argument) return null
      args.push(argument)

      this.skipTrivia()
      if (this.current() === ',') {
        this.index += 1
        this.skipTrivia()
        if (this.current() === ')') {
          this.index += 1
          return { kind: 'function', name, args }
        }
        continue
      }

      if (this.current() === ')') {
        this.index += 1
        return { kind: 'function', name, args }
      }

      return null
    }

    return null
  }

  private parseString(): AstNode | null {
    const value = this.readQuotedString(1)
    return value === null ? null : { kind: 'string', value }
  }

  private parseParamString(): AstNode | null {
    const start = this.index
    const value = this.readQuotedString(2, true)
    if (value !== null) return { kind: 'string', value }

    this.index = start
    return this.parseRawParamString()
  }

  private readQuotedString(offset: number, rejectInterpolated = false): string | null {
    this.index += offset

    let value = ''
    while (!this.isEnd()) {
      const char = this.current()

      if (char === '"' && !this.isEscaped(this.index)) {
        this.index += 1
        return value
      }

      if (rejectInterpolated && char === '{' && !this.isEscaped(this.index)) {
        return null
      }

      if (char === '\\') {
        this.index += 1
        if (this.isEnd()) return null

        const escaped = this.current()
        value += escaped === 'n'
          ? '\n'
          : escaped === 'r'
            ? '\r'
            : escaped === 't'
              ? '\t'
              : escaped
        this.index += 1
        continue
      }

      value += char
      this.index += 1
    }

    return null
  }

  private parseRawParamString(): AstNode | null {
    const start = this.index
    this.index += 2

    while (!this.isEnd()) {
      if (this.current() === '"' && !this.isEscaped(this.index)) {
        this.index += 1
        return { kind: 'raw', value: this.rule.slice(start, this.index) }
      }

      this.index += 1
    }

    this.index = start
    return null
  }

  private parseRawDelimited(open: string, close: string): AstNode | null {
    const start = this.index
    let depth = 0

    while (!this.isEnd()) {
      const char = this.current()
      const next = this.rule[this.index + 1]

      if (char === '"' && !this.isEscaped(this.index)) {
        this.index += 1
        while (!this.isEnd()) {
          if (this.current() === '"' && !this.isEscaped(this.index)) {
            this.index += 1
            break
          }
          this.index += 1
        }
        continue
      }

      if (char === '/' && next === '/') {
        const nextLine = this.rule.indexOf('\n', this.index + 2)
        this.index = nextLine === -1 ? this.rule.length : nextLine + 1
        continue
      }

      if (char === '/' && next === '*') {
        const commentEnd = this.rule.indexOf('*/', this.index + 2)
        if (commentEnd === -1) return null
        this.index = commentEnd + 2
        continue
      }

      if (char === open) depth += 1
      else if (char === close) {
        depth -= 1
        if (depth === 0) {
          this.index += 1
          return { kind: 'raw', value: this.rule.slice(start, this.index) }
        }
      }

      this.index += 1
    }

    this.index = start
    return null
  }

  private parseLiteral(): AstNode | null {
    const numberMatch = this.remaining().match(/^(?:0x[\da-f]+|-?\d+(?:\.\d+)?)/i)
    if (numberMatch) {
      this.index += numberMatch[0].length
      return { kind: 'number', value: numberMatch[0] }
    }

    const literalMatch = this.remaining().match(/^(true|false|null)(?![\w@$-])/i)
    if (!literalMatch) return null

    this.index += literalMatch[0].length
    const literal = literalMatch[1].toLowerCase()
    if (literal === 'null') return { kind: 'null' }

    return { kind: 'bool', value: literal === 'true' }
  }

  private parseField(): AstNode | null {
    if (!/[\w@$]/.test(this.current())) return null

    const start = this.index
    let lastNonWhitespace = start - 1

    while (!this.isEnd()) {
      if (this.startsComment() || this.startsOperator()) break

      const char = this.current()
      if (char === '\n' || char === '\r' || char === '"' || char === '=') break
      if ('(),[]{}'.includes(char)) break
      if (!/[\w\d@$\-:. ]/.test(char)) break

      if (!/\s/.test(char)) lastNonWhitespace = this.index
      this.index += 1
    }

    if (lastNonWhitespace < start) {
      this.index = start
      return null
    }

    const value = this.rule.slice(start, lastNonWhitespace + 1).trim()
    this.index = lastNonWhitespace + 1
    return { kind: 'field', value }
  }

  private consumeNamedParameter() {
    const match = this.remaining().match(/^\w+\s*=(?!=)/)
    if (match) this.index += match[0].length
  }

  private skipTrivia() {
    while (!this.isEnd()) {
      while (!this.isEnd() && /\s/.test(this.current())) this.index += 1

      if (this.rule.startsWith('//', this.index)) {
        const nextLine = this.rule.indexOf('\n', this.index + 2)
        this.index = nextLine === -1 ? this.rule.length : nextLine + 1
        continue
      }

      if (this.rule.startsWith('/*', this.index)) {
        const commentEnd = this.rule.indexOf('*/', this.index + 2)
        if (commentEnd === -1) {
          this.index = this.rule.length
          return
        }

        this.index = commentEnd + 2
        continue
      }

      break
    }
  }

  private consume(value: string): boolean {
    if (!this.rule.startsWith(value, this.index)) return false

    this.index += value.length
    return true
  }

  private startsComment(): boolean {
    return this.rule.startsWith('//', this.index) || this.rule.startsWith('/*', this.index)
  }

  private startsOperator(): boolean {
    return ['>=', '<=', '==', '!=', '&&', '||', '>', '<', '+'].some((operator) =>
      this.rule.startsWith(operator, this.index),
    )
  }

  private current(): string {
    return this.rule[this.index] ?? ''
  }

  private remaining(): string {
    return this.rule.slice(this.index)
  }

  private isEnd(): boolean {
    return this.index >= this.rule.length
  }

  private isEscaped(index: number): boolean {
    let slashCount = 0

    for (let cursor = index - 1; cursor >= 0 && this.rule[cursor] === '\\'; cursor -= 1)
      slashCount += 1

    return slashCount % 2 === 1
  }
}

function isFunction(ast: AstNode, name: string): ast is Extract<AstNode, { kind: 'function' }> {
  return ast.kind === 'function' && ast.name.toLowerCase() === name.toLowerCase()
}

function isTransparentWrapper(ast: AstNode): boolean {
  if (ast.kind !== 'function' || ast.args.length !== 1) return false

  const name = ast.name.toLowerCase()
  return name === 'joinref' || name.startsWith('coerce')
}

function literalValue(ast: AstNode): string | null {
  if (ast.kind === 'string' || ast.kind === 'number') return ast.value
  if (ast.kind === 'bool') return ast.value ? 'true' : 'false'
  if (ast.kind === 'field') return ast.value
  return null
}

function astToRule(ast: AstNode): string {
  switch (ast.kind) {
    case 'bool':
      return ast.value ? 'true' : 'false'
    case 'field':
    case 'number':
    case 'raw':
      return ast.value
    case 'function':
      return `${ast.name}(${ast.args.map(astToRule).join(', ')})`
    case 'null':
      return 'null'
    case 'string':
      return quote(ast.value)
  }
}

function pathFromField(value: string): string[] {
  return value
    .split('.')
    .map((part) => part.trim())
    .filter(Boolean)
}

function toRuleValue(ast: AstNode): Expression | null {
  if (isFunction(ast, 'CoerceDateTime') && ast.args.length === 1) {
    const inner = ast.args[0]
    if (inner.kind === 'string') return { Kind: 'Date', Value: inner.value }

    return toRuleValue(inner)
  }

  if (isFunction(ast, 'Graph') && ast.args.length === 1) {
    const value = literalValue(ast.args[0])
    return value === null ? null : { Kind: 'Reference', Value: value }
  }

  if (ast.kind === 'function' && isTransparentWrapper(ast)) return toRuleValue(ast.args[0])

  switch (ast.kind) {
    case 'field':
      return { Kind: 'Path', Path: pathFromField(ast.value) }
    case 'string':
      return { Kind: 'String', Value: ast.value }
    case 'number':
      return { Kind: 'Number', Value: ast.value }
    case 'bool':
      return { Kind: 'Bool', Value: ast.value }
    case 'null':
      return { Kind: 'Null', Value: null }
    case 'function':
    case 'raw':
      return { Kind: 'Expression', Value: astToRule(ast) }
    default:
      return null
  }
}

function normalizeRightValue(operator: string, right: Expression): Expression {
  if (
    (operator === 'HasMember' || operator === 'HasResource') &&
    right.Kind === 'String' &&
    'Value' in right
  ) {
    return { Kind: 'Reference', Value: right.Value }
  }

  return right
}

function toTreeNode(ast: AstNode): RuleTree | RuleClause | null {
  if (ast.kind !== 'function') return null

  const treeKind = treeKinds[ast.name.toLowerCase()]
  if (treeKind) {
    const treeNodes = ast.args.map(toTreeNode)
    if (treeNodes.some((node) => node === null)) return null

    return {
      $type: RuleType.RuleTree,
      Kind: treeKind,
      TreeNodes: treeNodes as (RuleTree | RuleClause)[],
    }
  }

  const operator = operatorNames[ast.name.toLowerCase()] ?? ast.name
  if (ast.args.length !== 2) return null

  const left = toRuleValue(ast.args[0])
  const right = toRuleValue(ast.args[1])
  if (!left || !right || left.Kind !== 'Path') return null

  return {
    $type: RuleType.RuleClause,
    Left: left as PathExpression,
    Operator: operator,
    Right: normalizeRightValue(operator, right),
  }
}

export function parseRuleFilterToRuleTree(rule: string): RuleTree | null {
  if (!rule.trim()) return null

  const ast = new RuleExpressionParser(rule).parse()
  if (!ast) return null

  const node = toTreeNode(ast)
  if (!node) return null

  if ('TreeNodes' in node) return node

  return {
    $type: RuleType.RuleTree,
    Kind: 'And',
    TreeNodes: [node],
  }
}

function optionValue(value: unknown): string | number | null {
  if (typeof value === 'string' || typeof value === 'number') return value

  if (value && typeof value === 'object') {
    const option = value as Record<string, unknown>
    const optionValue =
      option.value ?? option.Value ?? option.name ?? option.Name ?? option.key ?? option.Key

    return typeof optionValue === 'string' || typeof optionValue === 'number'
      ? optionValue
      : null
  }

  return null
}

function pathToRule(path: PathExpression['Path']): string | null {
  if (!Array.isArray(path) || path.length === 0) return null

  const parts = path.map(optionValue)
  if (parts.some((part) => part === null || part === '')) return null

  return parts.map(String).join('.')
}

function quote(value: unknown): string {
  return `"${String(value ?? '').replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
}

function referenceValue(value: unknown): string | number | null {
  if (Array.isArray(value)) return optionValue(value[0])

  return optionValue(value)
}

function valueToRule(value: Expression): string | null {
  if (value.Kind === 'Path') return pathToRule((value as PathExpression).Path)

  if (!('Value' in value)) return null

  switch (value.Kind) {
    case 'Bool':
    case 'Boolean':
      return String(value.Value).toLowerCase() === 'true' || value.Value === true ? 'true' : 'false'
    case 'Date':
      return `CoerceDateTime(${quote(value.Value)})`
    case 'Null':
      return 'null'
    case 'Number':
      return String(value.Value)
    case 'Reference': {
      const reference = referenceValue(value.Value)
      return reference === null || reference === '' ? null : `Graph(${quote(reference)})`
    }
    case 'Expression':
      return String(value.Value ?? '')
    case 'Regex':
    case 'String':
      return quote(value.Value)
    default:
      return null
  }
}

function nodeToRule(node: RuleTree | RuleClause): string | null {
  if ('TreeNodes' in node) {
    const children = node.TreeNodes.map(nodeToRule)
    if (children.some((child) => child === null)) return null

    const kind = node.Kind || 'And'
    if (!serializableTreeKinds.has(kind)) return null

    const childRules = children.join(', ')
    return kind === 'Not' ? `Not(Or(${childRules}))` : `${kind}(${childRules})`
  }

  const left = valueToRule(node.Left)
  const right = valueToRule(node.Right)
  if (!left || !right || !node.Operator) return null

  return `${node.Operator}(${left}, ${right})`
}

export function translateRuleTreeToRuleFilter(
  ruleTree: RuleTree | null | undefined,
): string | null {
  if (!ruleTree) return ''

  return nodeToRule(ruleTree)
}
